import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import InputAdornment from "@mui/material/InputAdornment";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import HelpOutline from "@mui/icons-material/HelpOutline";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { MuiMaterials } from "../@index.js";
import * as global from "../data/global";
import { createApiEndpoint } from "../index.js";
import {
    ModalChrome,
    ModalBody,
    ModalContentTile,
    SiteSelectPhase,
    SITE_WINDOW_IDS,
    ALL_SITES_OPTION,
    outlinedActionButtonSx,
    containedActionButtonSx,
    comparisonFieldSx,
    CenteredLoadingOverlay,
    ensureSiteInfosLoaded,
    ConfirmDialog,
    WindowChromeStatusBanner,
    getWindowChromeThemeColor,
} from "./windowChrome/index.js";

function utf16leBase64(text) {
    const bytes = new Uint8Array(text.length * 2);
    for (let i = 0; i < text.length; i++) {
        const code = text.charCodeAt(i);
        bytes[i * 2] = code & 0xff;
        bytes[i * 2 + 1] = (code >> 8) & 0xff;
    }
    let binary = "";
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}

function utf8Base64(text) {
    return btoa(unescape(encodeURIComponent(text)));
}

/** Opens SSH through the grainwave-ssh: handler installed on this PC. No downloaded file. */
function launchSshSession({ sshUser, host, password, privateKey, mode }) {
    const payload = utf8Base64(JSON.stringify({
        user: sshUser || "",
        host: host || "",
        password: password || "",
        privateKey: privateKey || "",
        mode: mode || "ssh",
    }));
    return new Promise((resolve) => {
        let opened = false;
        const onBlur = () => { opened = true; };
        window.addEventListener("blur", onBlur);
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = "grainwave-ssh:" + encodeURIComponent(payload);
        document.body.appendChild(iframe);
        setTimeout(() => {
            window.removeEventListener("blur", onBlur);
            iframe.remove();
            resolve(opened);
        }, 1200);
    });
}

/** Downloads a .cmd that opens PowerShell and starts ssh. The password is not on the ssh command line. */
function downloadSshCommand({ computerName, sshUser, host, password }) {
    const passwordB64 = utf8Base64(password);
    const title = String(computerName || host || "SSH").replace(/"/g, "");
    const user = String(sshUser || "").replace(/'/g, "");
    const target = String(host || "").replace(/'/g, "");
    const script = `
$ErrorActionPreference = 'Stop'
$b64 = '${passwordB64}'
$ask = Join-Path $env:TEMP ('gw-ask-' + [guid]::NewGuid().ToString('n') + '.cmd')
$inner = "Write-Output ([Text.Encoding]::UTF8.GetString([Convert]::FromBase64String('$b64')))"
$line = 'powershell -NoProfile -Command "' + $inner + '"'
Set-Content -LiteralPath $ask -Value @('@echo off', $line) -Encoding Ascii
$env:SSH_ASKPASS = $ask
$env:SSH_ASKPASS_REQUIRE = 'force'
$env:DISPLAY = '1'
$ssh = Join-Path $env:WINDIR 'System32\\OpenSSH\\ssh.exe'
if (-not (Test-Path -LiteralPath $ssh)) { $ssh = 'ssh' }
Write-Host 'Connecting ${user}@${target}'
try {
  & $ssh -o StrictHostKeyChecking=accept-new -o PreferredAuthentications=keyboard-interactive,password '${user}@${target}'
} finally {
  Remove-Item -LiteralPath $ask -Force -ErrorAction SilentlyContinue
}
`;
    const encoded = utf16leBase64(script.replace(/^\n/, ""));
    const cmd = `@echo off\r\nstart "SSH ${title}" powershell -NoExit -NoProfile -ExecutionPolicy Bypass -EncodedCommand ${encoded}\r\n`;
    const safe = title.replace(/[^\w.-]+/g, "_") || "computer";
    const blob = new Blob([cmd], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `SSH-${safe}.cmd`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function textWidth(text) {
    if (typeof document === "undefined") return String(text || "").length * 8;
    const canvas = textWidth.canvas || (textWidth.canvas = document.createElement("canvas"));
    const context = canvas.getContext("2d");
    if (!context) return String(text || "").length * 8;
    context.font = "14px Roboto, Helvetica, Arial, sans-serif";
    return context.measureText(String(text || "")).width;
}

function rowKey(row, index) {
    if (row.headscaleNodeId) return `node-${row.headscaleNodeId}`;
    if (row.id) return `id-${row.id}`;
    return `new-${index}`;
}

/** Two Headscale computers can share a name. Never let them share one saved login in the form. */
function RemoteConnectHelpTooltip({ textColor }) {
    const section = { fontWeight: 600, display: "block", mt: 1, "&:first-of-type": { mt: 0 } };
    return (
        <MuiMaterials.Tooltip
            arrow
            placement="bottom-start"
            title={(
                <MuiMaterials.Box component="div" sx={{ fontSize: 12, lineHeight: 1.45, maxWidth: 400 }}>
                    <MuiMaterials.Box component="span" sx={section}>SSH</MuiMaterials.Box>
                    Opens a PowerShell console and establishes a network tunnel to the site computer.
                    With the tunnel up you can reach devices on that site as if they were on your network (similar to NetEdit).
                    After TemputechService installs the GrainWave key, SSH logs in as root on Linux or grainwave on Windows. No saved password is required.
                    <MuiMaterials.Box component="span" sx={section}>RDP</MuiMaterials.Box>
                    Connects through Remote Desktop. Only one viewer at a time; other users who were logged in may be signed out.
                    <MuiMaterials.Box component="span" sx={section}>VNC</MuiMaterials.Box>
                    Connects through TightVNC. Multiple people can view the same screen at once; the local monitor stays visible.
                    <MuiMaterials.Box component="span" sx={section}>App requirements — Windows PC</MuiMaterials.Box>
                    Use GrainWave in Chrome or Edge on the PC where Remote was set up. Allow the GrainWave link when asked.
                    SSH uses OpenSSH; RDP uses Remote Desktop Connection; VNC uses TightVNC Viewer (installed on first use if needed).
                    <MuiMaterials.Box component="span" sx={section}>App requirements — Linux</MuiMaterials.Box>
                    SSH, RDP, and VNC from the Remote buttons are not launched from the browser. Use an SSH client on the tailnet
                    (saved user and password from Remote), or Remmina / a VNC viewer pointed at the tailnet address.
                    <MuiMaterials.Box component="span" sx={section}>App requirements — iPhone / iPad</MuiMaterials.Box>
                    The Remote buttons do not open apps from Safari. Use a tailnet VPN app plus SSH (e.g. Termius or Blink),
                    Microsoft Remote Desktop, or a VNC app with the password shown in Remote (eye icon).
                    <MuiMaterials.Box component="span" sx={section}>App requirements — Android</MuiMaterials.Box>
                    Same as iOS: use Tailscale, then SSH (Termius or similar), Microsoft Remote Desktop, or a VNC client
                    with the saved tailnet host and VNC password from Remote.
                </MuiMaterials.Box>
            )}
            componentsProps={{
                tooltip: {
                    sx: { maxWidth: 420, fontSize: 12, lineHeight: 1.45 },
                },
            }}
        >
            <MuiMaterials.IconButton
                size="small"
                aria-label="Remote connect help"
                sx={{ color: textColor, flexShrink: 0 }}
            >
                <HelpOutline fontSize="small" />
            </MuiMaterials.IconButton>
        </MuiMaterials.Tooltip>
    );
}

function formatStatusAtLocal(iso) {
    if (!iso) return "";
    const text = String(iso);
    const hasZone = /Z$|[+-]\d{2}:\d{2}$/.test(text);
    const at = new Date(hasZone ? text : `${text}Z`);
    return Number.isNaN(at.getTime()) ? "" : at.toLocaleString();
}

/**
 * Headscale only knows the node answers. TemputechService reports whether SSH, Remote Desktop, and TightVNC
 * actually came up, which is the difference between "Online" and a row whose buttons all fail.
 */
function ServiceStatusLine({ row, muted, offlineColor }) {
    const status = row.serviceStatus;
    if (!status) return null;

    const parts = [
        { label: "SSH", outcome: status.ssh, detail: status.sshDetail },
        { label: "RDP", outcome: status.rdp, detail: status.rdpDetail },
        { label: "VNC", outcome: status.vnc, detail: status.vncDetail },
    ].filter((part) => part.outcome);
    if (parts.length === 0) return null;

    const broken = parts.filter((part) => part.outcome !== "listening");
    const reported = formatStatusAtLocal(row.serviceStatusAtUtc);
    const tip = (
        <MuiMaterials.Box component="div" sx={{ fontSize: 12, lineHeight: 1.45, maxWidth: 420 }}>
            {parts.map((part) => (
                <MuiMaterials.Box component="div" key={part.label} sx={{ mb: 0.5 }}>
                    <strong>{part.label}: {part.outcome}</strong>
                    {part.detail ? <> — {part.detail}</> : null}
                </MuiMaterials.Box>
            ))}
            {status.elevated === false ? (
                <MuiMaterials.Box component="div" sx={{ mt: 0.5 }}>
                    TemputechService is not running elevated, so it cannot set any of these up.
                </MuiMaterials.Box>
            ) : null}
            {status.rebootPending === true ? (
                <MuiMaterials.Box component="div" sx={{ mt: 0.5 }}>
                    Windows is waiting for a reboot. Restart this PC to finish installing updates and OpenSSH.
                </MuiMaterials.Box>
            ) : null}
            <MuiMaterials.Box component="div" sx={{ mt: 0.5 }}>
                {status.serviceVersion ? `Service ${status.serviceVersion}. ` : ""}
                {reported ? `Last reported ${reported}.` : ""}
            </MuiMaterials.Box>
        </MuiMaterials.Box>
    );

    return (
        <MuiMaterials.Tooltip arrow placement="bottom-start" title={tip}>
            <MuiMaterials.Typography
                sx={{
                    fontSize: "12px",
                    color: broken.length ? offlineColor : muted,
                    cursor: "help",
                    whiteSpace: "nowrap",
                }}
            >
                {broken.length
                    ? `service: ${broken.map((part) => `${part.label} ${part.outcome}`).join(", ")}`
                    : "service: SSH, RDP, VNC listening"}
                {status.rebootPending === true ? " — reboot needed" : ""}
            </MuiMaterials.Typography>
        </MuiMaterials.Tooltip>
    );
}

function separateSharedLogins(rows) {
    const seen = new Set();
    return rows.map((row) => {
        const id = row.id ? String(row.id) : "";
        const ownedByOther = row.savedNodeId
            && row.headscaleNodeId
            && String(row.savedNodeId) !== String(row.headscaleNodeId)
            && !row.ipv4;
        const duplicate = Boolean(id) && seen.has(id);
        if (id) seen.add(id);
        if (!ownedByOther && !duplicate) return row;
        return {
            ...row,
            id: "",
            computerName: row.headscaleName || "",
            siteGuid: "",
            sshUser: "",
            hasPassword: false,
            passwordDraft: "",
            knownPassword: "",
            showPassword: false,
        };
    });
}

const RemoteAccessModal = ({ isModalOpen, setIsModalOpen, filterToSiteGuid }) => {
    const open = Boolean(isModalOpen?.[0]);
    const [loading, setLoading] = useState(false);
    const [busyKey, setBusyKey] = useState("");
    const [rows, setRows] = useState([]);
    const [message, setMessage] = useState("");
    const [sites, setSites] = useState([]);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [savingAll, setSavingAll] = useState(false);
    const [editingNameKey, setEditingNameKey] = useState("");
    const [editingUserKey, setEditingUserKey] = useState("");
    const [editingPasswordKey, setEditingPasswordKey] = useState("");
    const [pickerSiteGuid, setPickerSiteGuid] = useState("");
    const [selectedSiteGuid, setSelectedSiteGuid] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        setMessage("");
        try {
            await ensureSiteInfosLoaded?.();
            const catalog = (global.gw.siteInfos || [])
                .map((site) => ({
                    guid: site.siteGuid,
                    name: site.siteName || site.main?.siteName || site.siteGuid,
                }))
                .filter((site) => site.guid)
                .sort((a, b) => String(a.name).localeCompare(String(b.name)));
            setSites(catalog);
            const result = await createApiEndpoint().fetchRemoteAccess("direct");
            if (!result?.ok) {
                setMessage(result?.error || "Could not load remote computers.");
                setRows([]);
                return;
            }
            if (result.warning) setMessage(result.warning);
            setRows(separateSharedLogins(result.rows || []).map((row, index) => ({
                ...row,
                key: rowKey(row, index),
                passwordDraft: row.passwordDraft || "",
                baseline: {
                    computerName: row.computerName || "",
                    siteGuid: row.siteGuid || "",
                    sshUser: row.sshUser || "",
                },
            })));
        } catch (err) {
            setMessage(err?.message || "Could not load remote computers.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!open) {
            setPickerSiteGuid("");
            setSelectedSiteGuid("");
            return;
        }
        if (filterToSiteGuid) {
            setPickerSiteGuid(filterToSiteGuid);
            setSelectedSiteGuid(filterToSiteGuid);
        }
        load();
    }, [open, filterToSiteGuid, load]);

    useLayoutEffect(() => {
        if (!open || !filterToSiteGuid) return;
        setPickerSiteGuid(filterToSiteGuid);
        setSelectedSiteGuid(filterToSiteGuid);
    }, [open, filterToSiteGuid]);

    const patch = (key, change) => {
        setRows((current) => current.map((row) => (row.key === key ? { ...row, ...change } : row)));
    };

    const persistRow = (row) => createApiEndpoint().saveRemoteAccess("direct", {
        id: row.id || "",
        computerName: row.computerName || "",
        sshUser: row.sshUser || "",
        headscaleName: row.headscaleName || "",
        headscaleNodeId: row.headscaleNodeId || 0,
        siteGuid: row.siteGuid || "",
        password: row.passwordDraft || "",
    });

    const isRowDirty = (row) => {
        if (!row.baseline) return Boolean(String(row.computerName || row.headscaleName || "").trim());
        return (row.computerName || "") !== row.baseline.computerName
            || (row.siteGuid || "") !== row.baseline.siteGuid
            || (row.sshUser || "") !== row.baseline.sshUser
            || Boolean(row.passwordDraft);
    };

    const dirtyRows = rows.filter(isRowDirty);

    const save = async (row) => {
        setBusyKey(row.key);
        setMessage("");
        const result = await persistRow(row);
        setBusyKey("");
        if (!result?.ok) {
            setMessage(result?.error || "Save failed.");
            return;
        }
        const warning = result.warning || "";
        await load();
        if (warning) setMessage(warning);
    };

    const saveAll = async () => {
        const pending = rows.filter(isRowDirty);
        if (!pending.length) return;
        setSavingAll(true);
        setMessage("");
        let warning = "";
        for (const row of pending) {
            setBusyKey(row.key);
            const result = await persistRow(row);
            if (!result?.ok) {
                setBusyKey("");
                setSavingAll(false);
                setMessage(result?.error || "Save failed.");
                return;
            }
            if (result.warning) warning = result.warning;
        }
        setBusyKey("");
        setSavingAll(false);
        await load();
        if (warning) setMessage(warning);
    };

    const closeDeleteConfirm = () => {
        if (deleting) return;
        setDeleteTarget(null);
    };

    const confirmDelete = async () => {
        const row = deleteTarget;
        if (!row) return;
        const deleteId = row.id || (row.headscaleNodeId ? `n:${row.headscaleNodeId}` : "");
        if (!deleteId) {
            setRows((current) => current.filter((item) => item.key !== row.key));
            setDeleteTarget(null);
            return;
        }
        setDeleting(true);
        setBusyKey(row.key);
        const ok = await createApiEndpoint().deleteRemoteAccess("direct", deleteId);
        setDeleting(false);
        setBusyKey("");
        setDeleteTarget(null);
        if (!ok) {
            setMessage("Delete failed.");
            return;
        }
        await load();
    };

    const togglePassword = async (row) => {
        if (row.showPassword) {
            patch(row.key, { showPassword: false });
            return;
        }
        if (row.passwordDraft || row.knownPassword || !row.id || !row.hasPassword) {
            patch(row.key, { showPassword: true });
            return;
        }
        setBusyKey(row.key);
        setMessage("");
        const result = await createApiEndpoint().connectRemoteAccess("direct", row.id);
        setBusyKey("");
        if (!result?.ok || !result.password) {
            setMessage(result?.error || "Could not load the password.");
            return;
        }
        patch(row.key, { knownPassword: result.password, showPassword: true });
    };

    const connect = async (row, mode) => {
        if (!row.id) {
            setMessage("Save this computer before connecting.");
            return;
        }
        setBusyKey(row.key);
        setMessage("");
        const result = await createApiEndpoint().connectRemoteAccess("direct", row.id);
        setBusyKey("");
        if (!result?.ok) {
            setMessage(result?.error || "Connect failed.");
            return;
        }
        if (mode === "vnc" && !result.vncPassword) {
            setMessage("TightVNC is not set up on this computer yet. It is installed with the next service update.");
            return;
        }
        if (mode === "ssh" && result.sshPrivateKey && result.sshKeyUser) {
            const opened = await launchSshSession({
                sshUser: result.sshKeyUser,
                host: result.host,
                privateKey: result.sshPrivateKey,
                mode: "ssh",
            });
            if (!opened) {
                setMessage("Choose Open on the GrainWave SSH prompt. Check always allow so the next connect opens straight into the session.");
            }
            return;
        }
        if (mode !== "vnc" && (!result.sshUser || !result.password)) {
            setMessage(result?.error || "password is empty");
            return;
        }
        if (/Windows/i.test(navigator.userAgent || "")) {
            const opened = await launchSshSession(mode === "vnc"
                ? { sshUser: "vnc", host: result.host, password: result.vncPassword, mode: "vnc" }
                : { ...result, mode });
            if (!opened) {
                const prompt = mode === "vnc"
                    ? "Choose Open on the GrainWave SSH prompt to start TightVNC. Check always allow so the next connect opens straight in."
                    : mode === "rdp"
                        ? "Choose Open on the GrainWave SSH prompt to start Remote Desktop. Check always allow so the next connect opens straight in."
                        : "Choose Open on the GrainWave SSH prompt. Check always allow so the next connect opens straight into the session.";
                setMessage(prompt);
            }
        } else if (mode === "rdp" || mode === "vnc") {
            setMessage(mode === "vnc"
                ? "TightVNC launch is available from Windows."
                : "Remote Desktop launch is available from Windows.");
        } else {
            downloadSshCommand(result);
        }
    };

    const addBlank = () => {
        const key = `new-${Date.now()}`;
        const assignedSite = activeSiteGuid && activeSiteGuid !== ALL_SITES_OPTION ? activeSiteGuid : "";
        setEditingNameKey(key);
        setRows((current) => [
            ...current,
            {
                key,
                id: "",
                computerName: "",
                siteGuid: assignedSite,
                headscaleName: "",
                headscaleNodeId: 0,
                sshUser: "",
                passwordDraft: "",
                hasPassword: false,
                ipv4: "",
                online: false,
            },
        ]);
    };

    const themeText = getWindowChromeThemeColor("#333333", "#f4cc8e");
    const muted = getWindowChromeThemeColor("#666666", "#a8abb1");
    const cardBg = getWindowChromeThemeColor("#ffffff", "#2d2d2d");
    const cardBorder = getWindowChromeThemeColor("#e0e0e0", "#3d3d3d");
    const onlineColor = "#4caf50";
    const offlineColor = "#ef5350";
    const fieldSx = {
        minWidth: 0,
        width: "100%",
        ...comparisonFieldSx(),
        "& .MuiInputBase-root": { minWidth: 0 },
    };
    const editorFieldSx = { ...fieldSx, width: "auto", flex: "1 1 140px" };
    const longestSite = sites.reduce((max, site) => Math.max(max, textWidth(site.name)), textWidth("None"));
    const siteMenuWidth = Math.ceil(longestSite) + 56;
    const deleteName = deleteTarget?.computerName || deleteTarget?.headscaleName || "this computer";
    const statusColor = (row) => (row.headscaleName ? (row.online ? onlineColor : offlineColor) : muted);
    const statusLabel = (row) => (row.headscaleName ? (row.online ? "Online" : "Offline") : "—");

    const siteNameFor = (row) => {
        const match = sites.find((site) => site.guid === row.siteGuid);
        return match?.name || row.siteName || "";
    };
    const orderedRows = [...rows].sort((a, b) => {
        const aName = siteNameFor(a);
        const bName = siteNameFor(b);
        if (!aName && bName) return 1;
        if (aName && !bName) return -1;
        const bySite = aName.localeCompare(bName, undefined, { sensitivity: "base" });
        if (bySite) return bySite;
        return String(a.computerName || a.headscaleName || "").localeCompare(
            String(b.computerName || b.headscaleName || ""),
            undefined,
            { sensitivity: "base" },
        );
    });

    const sameSite = (left, right) => {
        const norm = global.normalizeGuidForCompare;
        const a = norm?.(left) ?? String(left || "").toLowerCase();
        const b = norm?.(right) ?? String(right || "").toLowerCase();
        return Boolean(a) && a === b;
    };
    const activeSiteGuid = selectedSiteGuid || (open ? filterToSiteGuid : "") || "";
    const phase = activeSiteGuid ? "edit" : "select";
    const isAllSites = activeSiteGuid === ALL_SITES_OPTION;
    const visibleRows = isAllSites || !activeSiteGuid
        ? orderedRows
        : orderedRows.filter((row) => sameSite(row.siteGuid, activeSiteGuid));

    const handleSiteChange = (guid) => {
        if (dirtyRows.length > 0 && guid !== activeSiteGuid) {
            setMessage("Save changes before switching sites.");
            return;
        }
        setPickerSiteGuid(guid);
        setSelectedSiteGuid(guid);
    };

    const rowActions = (row) => (
        <MuiMaterials.Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
            <MuiMaterials.Button
                size="small"
                variant="contained"
                sx={containedActionButtonSx()}
                disabled={busyKey === row.key}
                onClick={() => save(row)}
            >
                Save
            </MuiMaterials.Button>
            {row.shellEnabled !== false && (
                <>
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={outlinedActionButtonSx()}
                        disabled={busyKey === row.key || !row.id || !row.ipv4}
                        onClick={() => connect(row, "ssh")}
                    >
                        SSH
                    </MuiMaterials.Button>
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={outlinedActionButtonSx()}
                        disabled={busyKey === row.key || !row.id || !row.ipv4}
                        onClick={() => connect(row, "rdp")}
                    >
                        RDP
                    </MuiMaterials.Button>
                    <MuiMaterials.Button
                        size="small"
                        variant="outlined"
                        sx={outlinedActionButtonSx()}
                        disabled={busyKey === row.key || !row.id || !row.ipv4}
                        onClick={() => connect(row, "vnc")}
                    >
                        VNC
                    </MuiMaterials.Button>
                </>
            )}
            <MuiMaterials.Tooltip title="Delete" arrow>
                <span>
                    <MuiMaterials.IconButton size="small" aria-label="Delete" disabled={busyKey === row.key || deleting} onClick={() => setDeleteTarget(row)} sx={{ color: themeText }}>
                        <DeleteOutline fontSize="small" />
                    </MuiMaterials.IconButton>
                </span>
            </MuiMaterials.Tooltip>
        </MuiMaterials.Box>
    );

    const siteField = (row) => (
        <MuiMaterials.TextField
            select
            value={row.siteGuid || ""}
            size="small"
            variant="outlined"
            sx={{ ...fieldSx, width: "100%", maxWidth: 320, minWidth: 180, flex: "none" }}
            SelectProps={{
                MenuProps: {
                    PaperProps: { sx: { minWidth: Math.max(siteMenuWidth, 180) } },
                },
            }}
            onChange={(e) => patch(row.key, { siteGuid: e.target.value })}
        >
            <MuiMaterials.MenuItem value="" sx={{ whiteSpace: "nowrap" }}>None</MuiMaterials.MenuItem>
            {sites.map((site) => (
                <MuiMaterials.MenuItem key={site.guid} value={site.guid} sx={{ whiteSpace: "nowrap" }}>
                    {site.name}
                </MuiMaterials.MenuItem>
            ))}
        </MuiMaterials.TextField>
    );

    const passwordField = (row) => (
        <MuiMaterials.TextField
            autoFocus
            type={row.showPassword ? "text" : "password"}
            value={row.passwordDraft || row.knownPassword || ""}
            placeholder={row.hasPassword ? "••••••••" : "Password"}
            size="small"
            variant="outlined"
            autoComplete="new-password"
            sx={fieldSx}
            onChange={(e) => patch(row.key, { passwordDraft: e.target.value, knownPassword: "" })}
            onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
            }}
            InputProps={{
                endAdornment: (
                    <InputAdornment position="end">
                        <MuiMaterials.IconButton
                            size="small"
                            edge="end"
                            aria-label={row.showPassword ? "Hide password" : "Show password"}
                            disabled={busyKey === row.key}
                            onClick={() => togglePassword(row)}
                        >
                            {row.showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </MuiMaterials.IconButton>
                    </InputAdornment>
                ),
            }}
        />
    );

    return (
        <>
            <ModalChrome
                open={open}
                onClose={() => setIsModalOpen([false])}
                title="Remote"
                toolTitle="Remote"
                siteWindowId={SITE_WINDOW_IDS.remote}
                siteGuid={activeSiteGuid || null}
                showHeaderSiteSelect={phase === "edit"}
                onHeaderSiteChange={handleSiteChange}
                headerIncludeAllSites
                statusBanner={message ? (
                    <WindowChromeStatusBanner status={message} onDismiss={() => setMessage("")} />
                ) : null}
                loadingOverlay={phase === "edit" && loading ? <CenteredLoadingOverlay /> : null}
                actions={phase === "edit" ? (
                    <MuiMaterials.Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                        <RemoteConnectHelpTooltip textColor={themeText} />
                        <MuiMaterials.Button
                            size="small"
                            variant="contained"
                            disabled={loading || savingAll || dirtyRows.length === 0}
                            onClick={saveAll}
                            sx={{ whiteSpace: "nowrap", flexShrink: 0, ...containedActionButtonSx() }}
                        >
                            {savingAll ? "Saving…" : dirtyRows.length > 0 ? `Save (${dirtyRows.length})` : "Save"}
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            disabled={loading || savingAll}
                            onClick={() => {
                                if (dirtyRows.length > 0) {
                                    setMessage("Save changes before refreshing.");
                                    return;
                                }
                                load();
                            }}
                            sx={{ whiteSpace: "nowrap", flexShrink: 0, ...outlinedActionButtonSx() }}
                        >
                            Refresh
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            sx={outlinedActionButtonSx()}
                            onClick={addBlank}
                        >
                            Add
                        </MuiMaterials.Button>
                    </MuiMaterials.Box>
                ) : null}
            >
                {phase === "select" ? (
                    <SiteSelectPhase
                        value={pickerSiteGuid}
                        onChange={setPickerSiteGuid}
                        onContinue={(guid) => {
                            setPickerSiteGuid(guid);
                            setSelectedSiteGuid(guid);
                        }}
                        onCancel={() => setIsModalOpen([false])}
                        loading={loading}
                        includeAllSites
                    />
                ) : (
                <ModalBody>
                    <ModalContentTile>
                        <MuiMaterials.Box
                            className="hide-scrollbar"
                            sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}
                        >
                            {visibleRows.length === 0 && !loading ? (
                                <MuiMaterials.Box sx={{ padding: "28px 8px", textAlign: "center", color: muted }}>
                                    {isAllSites
                                        ? "No Headscale computers yet."
                                        : "No computers for this site. All Sites also lists computers that are not assigned to a site."}
                                </MuiMaterials.Box>
                            ) : visibleRows.map((row) => (
                                <MuiMaterials.Box
                                    key={row.key}
                                    sx={{
                                        backgroundColor: cardBg,
                                        border: `1px solid ${cardBorder}`,
                                        borderRadius: "8px",
                                        p: 1.5,
                                        mb: 1,
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 1.5,
                                        alignItems: "center",
                                    }}
                                >
                                    <MuiMaterials.Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
                                        {siteField(row)}
                                        <MuiMaterials.Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, minWidth: 0 }}>
                                            {editingNameKey === row.key || !(row.computerName || row.headscaleName) ? (
                                                <MuiMaterials.TextField
                                                    autoFocus
                                                    value={row.computerName || ""}
                                                    placeholder="Computer"
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ ...editorFieldSx, maxWidth: 280 }}
                                                    onChange={(e) => patch(row.key, { computerName: e.target.value })}
                                                    onBlur={() => setEditingNameKey("")}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") e.currentTarget.blur();
                                                    }}
                                                />
                                            ) : (
                                                <MuiMaterials.Typography
                                                    onClick={() => setEditingNameKey(row.key)}
                                                    sx={{ fontWeight: 600, color: themeText, fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", width: "fit-content", maxWidth: "100%", minWidth: 0 }}
                                                >
                                                    {row.computerName || row.headscaleName}
                                                </MuiMaterials.Typography>
                                            )}
                                            {row.ipv4 ? (
                                                <>
                                                    <MuiMaterials.Box sx={{ width: "1px", alignSelf: "stretch", minHeight: 14, bgcolor: cardBorder, flexShrink: 0 }} />
                                                    <MuiMaterials.Typography sx={{ color: muted, fontSize: "14px", flexShrink: 0 }}>
                                                        {row.ipv4}
                                                    </MuiMaterials.Typography>
                                                </>
                                            ) : null}
                                        </MuiMaterials.Box>
                                        {editingUserKey === row.key ? (
                                            <MuiMaterials.TextField
                                                autoFocus
                                                value={row.sshUser || ""}
                                                placeholder="User"
                                                size="small"
                                                variant="outlined"
                                                sx={{ ...fieldSx, maxWidth: 280, mt: 0.5 }}
                                                onChange={(e) => patch(row.key, { sshUser: e.target.value })}
                                                onBlur={() => setEditingUserKey("")}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") e.currentTarget.blur();
                                                }}
                                            />
                                        ) : (
                                            <MuiMaterials.Typography
                                                onClick={() => setEditingUserKey(row.key)}
                                                sx={{ color: muted, fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", width: "fit-content", maxWidth: "100%" }}
                                            >
                                                {row.sshUser || "User"}
                                            </MuiMaterials.Typography>
                                        )}
                                        {editingPasswordKey === row.key ? (
                                            <MuiMaterials.Box
                                                sx={{ maxWidth: 280, mt: 0.5 }}
                                                onBlur={(e) => {
                                                    const box = e.currentTarget;
                                                    if (e.relatedTarget && box.contains(e.relatedTarget)) return;
                                                    window.setTimeout(() => {
                                                        if (box.contains(document.activeElement)) return;
                                                        setEditingPasswordKey("");
                                                    }, 0);
                                                }}
                                            >
                                                {passwordField(row)}
                                            </MuiMaterials.Box>
                                        ) : (
                                            <MuiMaterials.Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0 }}>
                                                <MuiMaterials.Typography
                                                    onClick={() => setEditingPasswordKey(row.key)}
                                                    sx={{ color: muted, fontSize: "12px", letterSpacing: (row.showPassword && (row.passwordDraft || row.knownPassword)) ? "normal" : "0.12em", cursor: "pointer" }}
                                                >
                                                    {row.showPassword && (row.passwordDraft || row.knownPassword)
                                                        ? (row.passwordDraft || row.knownPassword)
                                                        : ((row.hasPassword || row.passwordDraft) ? "••••••••" : "Password")}
                                                </MuiMaterials.Typography>
                                                {(row.hasPassword || row.passwordDraft || row.knownPassword) ? (
                                                    <MuiMaterials.Tooltip title={row.showPassword ? "Hide password" : "Show password"} arrow>
                                                        <MuiMaterials.IconButton
                                                            size="small"
                                                            aria-label={row.showPassword ? "Hide password" : "Show password"}
                                                            disabled={busyKey === row.key}
                                                            onClick={() => togglePassword(row)}
                                                            sx={{ color: muted, p: 0.25 }}
                                                        >
                                                            {row.showPassword
                                                                ? <VisibilityOff sx={{ fontSize: 16 }} />
                                                                : <Visibility sx={{ fontSize: 16 }} />}
                                                        </MuiMaterials.IconButton>
                                                    </MuiMaterials.Tooltip>
                                                ) : null}
                                            </MuiMaterials.Box>
                                        )}
                                        <MuiMaterials.Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 }}>
                                            <MuiMaterials.Typography sx={{ color: statusColor(row), fontSize: "12px", fontWeight: 600 }}>
                                                {statusLabel(row)}
                                            </MuiMaterials.Typography>
                                            <ServiceStatusLine row={row} muted={muted} offlineColor={offlineColor} />
                                        </MuiMaterials.Box>
                                    </MuiMaterials.Box>
                                    <MuiMaterials.Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", flex: "0 0 auto", justifyContent: "flex-end" }}>
                                        {rowActions(row)}
                                    </MuiMaterials.Box>
                                </MuiMaterials.Box>
                            ))}
                        </MuiMaterials.Box>
                    </ModalContentTile>
                </ModalBody>
                )}
            </ModalChrome>
            <ConfirmDialog
                open={!!deleteTarget}
                onClose={closeDeleteConfirm}
                title="Delete this computer?"
                titleId="delete-remote-access-title"
                message={deleteTarget?.id
                    ? `This removes the saved login for ${deleteName}. This cannot be undone.`
                    : `Remove ${deleteName}?`}
                actions={(
                    <>
                        <MuiMaterials.Button
                            size="small"
                            variant="outlined"
                            disabled={deleting}
                            onClick={closeDeleteConfirm}
                            sx={outlinedActionButtonSx()}
                        >
                            Cancel
                        </MuiMaterials.Button>
                        <MuiMaterials.Button
                            size="small"
                            variant="contained"
                            disabled={deleting}
                            onClick={confirmDelete}
                            sx={containedActionButtonSx()}
                        >
                            {deleting ? "Deleting…" : "Delete"}
                        </MuiMaterials.Button>
                    </>
                )}
            />
        </>
    );
};

export default RemoteAccessModal;
